import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Botón "?" junto a la etiqueta de un campo que abre un mini popover con la
 * ayuda (pedido Eduardo 2026-08-29, patrón visto en Sleeper: "mucho texto por
 * todos lados, vamos a limpiar"). Sustituye a los `field-hint`/`muted`
 * permanentes: la etiqueta queda sola y la explicación aparece solo a
 * demanda. Se cierra con click fuera, Esc o segundo click en el "?".
 *
 * Posición (Eduardo 2026-09-05, móvil): el popover abría SIEMPRE hacia
 * arriba y en móvil se perdía detrás del header y del selector de
 * categoría (sticky), o lo recortaba la tabla con scroll. Ahora se pinta en
 * un portal sobre <body> con `position: fixed`, abre hacia ABAJO por
 * defecto y solo sube cuando abajo no cabe (dock inferior del torneo) y
 * arriba hay más espacio libre — medido entre el chrome fijo de arriba y el
 * de abajo. Horizontalmente se pega al borde del viewport sin salirse y la
 * flechita sigue apuntando al "?".
 *
 * Uso: envolver la etiqueta con `.hint-label` para que el "?" quede en el
 * mismo renglón aunque el <label> sea columna:
 *   <span className="hint-label">Sede<FieldHint>Explicación…</FieldHint></span>
 */
interface Props {
  children: ReactNode;
  label?: string;
}

export type HintPlacement = "below" | "above";

const EDGE = 8; // aire mínimo contra los bordes del viewport y del chrome
const GAP = 9; // flechita (7px) + un poco de aire respecto al "?"
const MIN_HEIGHT = 96; // por debajo de esto mejor scrollear dentro del popover

/** Límite inferior del chrome fijo de ARRIBA (header móvil, nav contextual
 * del torneo, banner de impersonación) en coordenadas del viewport. */
function chromeTopLimit(): number {
  let limit = 0;
  const half = window.innerHeight / 2;
  const nodes = document.querySelectorAll<HTMLElement>(
    ".app-header, .admin-tournament-context-nav, .impersonation-banner",
  );
  nodes.forEach((node) => {
    const position = window.getComputedStyle(node).position;
    if (position !== "fixed" && position !== "sticky") return;
    const rect = node.getBoundingClientRect();
    if (rect.height <= 0 || rect.top >= half) return;
    if (rect.bottom > limit) limit = rect.bottom;
  });
  return limit;
}

/** Límite superior del chrome fijo de ABAJO (dock de operación del torneo,
 * barras inferiores). Sin chrome, el borde del viewport. */
function chromeBottomLimit(): number {
  let limit = window.innerHeight;
  const half = window.innerHeight / 2;
  const nodes = document.querySelectorAll<HTMLElement>(
    ".tournament-operation-dock, .tournament-bottom-nav, .player-main-bottom-nav, .player-tournament-bottom-nav",
  );
  nodes.forEach((node) => {
    if (window.getComputedStyle(node).position !== "fixed") return;
    const rect = node.getBoundingClientRect();
    if (rect.height <= 0 || rect.top <= half) return;
    if (rect.top < limit) limit = rect.top;
  });
  return limit;
}

export interface HintPopLayout {
  placement: HintPlacement;
  top: number;
  left: number;
  maxHeight: number;
  arrowX: number;
}

/** Cálculo puro (probado en Vitest): decide arriba/abajo y pega el popover
 * al viewport. `popHeight` es la altura natural del popover sin recorte. */
export function computeHintPopLayout(input: {
  trigger: { top: number; bottom: number; left: number; width: number };
  popWidth: number;
  popHeight: number;
  viewportWidth: number;
  topLimit: number;
  bottomLimit: number;
}): HintPopLayout {
  const { trigger, popWidth, popHeight, viewportWidth, topLimit, bottomLimit } = input;
  const spaceBelow = bottomLimit - EDGE - trigger.bottom - GAP;
  const spaceAbove = trigger.top - GAP - (topLimit + EDGE);
  const placement: HintPlacement =
    spaceBelow >= popHeight || spaceBelow >= spaceAbove ? "below" : "above";
  const available = placement === "below" ? spaceBelow : spaceAbove;
  const maxHeight = Math.max(MIN_HEIGHT, Math.floor(available));
  const height = Math.min(popHeight, maxHeight);
  const top = placement === "below" ? trigger.bottom + GAP : trigger.top - GAP - height;
  const centerX = trigger.left + trigger.width / 2;
  const maxLeft = Math.max(EDGE, viewportWidth - popWidth - EDGE);
  const left = Math.min(Math.max(centerX - popWidth / 2, EDGE), maxLeft);
  return { placement, top, left, maxHeight, arrowX: centerX - left };
}

export default function FieldHint({ children, label = "Ayuda del campo" }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLSpanElement | null>(null);
  const popId = useId();

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const pop = popRef.current;
    if (!trigger || !pop) return;
    // Medir la altura natural: sin esto un maxHeight previo (scroll) lo
    // dejaría encogido para siempre.
    pop.style.maxHeight = "none";
    const rect = trigger.getBoundingClientRect();
    const layout = computeHintPopLayout({
      trigger: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
      popWidth: pop.offsetWidth,
      popHeight: pop.offsetHeight,
      viewportWidth: window.innerWidth,
      topLimit: chromeTopLimit(),
      bottomLimit: chromeBottomLimit(),
    });
    pop.style.top = `${Math.round(layout.top)}px`;
    pop.style.left = `${Math.round(layout.left)}px`;
    pop.style.maxHeight = `${layout.maxHeight}px`;
    pop.style.setProperty("--hint-arrow-x", `${Math.round(layout.arrowX)}px`);
    pop.dataset.placement = layout.placement;
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener("resize", reposition);
    // capture: también los contenedores con scroll propio (tablas).
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span className="hint" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="hint__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={(event) => {
          // Dentro de un <label>: sin esto el click también activa el input
          // asociado (abre el file picker, enfoca el campo, palomea el
          // checkbox) — el "?" solo debe abrir la ayuda.
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        ?
      </button>
      {open
        ? createPortal(
            <span className="hint__pop" role="tooltip" id={popId} ref={popRef} data-placement="below">
              <span className="hint__pop-body">{children}</span>
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
